import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Sun, Moon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Dark mode state and logic
  const [darkMode, setDarkMode] = useState(() => {
    if (localStorage.getItem("theme")) {
      return localStorage.getItem("theme") === "dark";
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  // Handle toggle sidebar
  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  // Navigation links
  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/about", label: "About" },
    { href: "/business", label: "Business" },
    { href: "/careers", label: "Careers" },
    { href: "/blogs", label: "Blogs" },
    { href: "/contact", label: "Contact" },
  ];

  // Render navigation link with active state
  const NavLink = ({ href, label, mobile = false }) => {
    const isActive = location.pathname === href;

    const linkClasses = mobile
      ? `py-2 px-4 rounded-md transition-all duration-300 font-medium ${isActive
        ? "bg-amber-500/20 dark:bg-amber-500/30 text-amber-700 dark:text-amber-300"
        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
      }`
      : `relative group font-medium transition-colors duration-300 ${isActive
        ? "text-amber-600 dark:text-amber-400"
        : "hover:text-amber-600 dark:hover:text-amber-400"
      }`;

    const desktopLinkStyle = mobile
      ? ""
      : `
      after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-amber-600 
      after:transition-all after:duration-300 
      ${isActive ? "after:w-full" : "group-hover:after:w-full"}
    `;

    return (
      <Link to={href} className={`${linkClasses} ${desktopLinkStyle}`}>
        {label}
      </Link>
    );
  };

  return (
    <>
      {/* Main Navbar */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${scrolled
            ? "bg-white dark:bg-gray-900 shadow-lg dark:shadow-gray-800/30 py-4 text-gray-800 dark:text-gray-200"
            : "bg-transparent py-4 text-gray-500 dark:text-gray-100"
          }`}
        data-aos="fade-down"
        data-aos-duration="800"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo with Company Name */}
            <Link
              to="/"
              className="flex-shrink-0 cursor-pointer transform transition-all duration-300 hover:scale-105 group"
            >
              <div className="flex items-center space-x-3">
                {/* Logo Image */}
                <div className="relative">
                  <img
                    src="https://ik.imagekit.io/zfayt6inj/lofgodb.jpg?updatedAt=1748852043440"
                    alt="Dev Bhoomi Group Logo"
                    className="h-16 w-16 object-contain rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/20 to-yellow-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Company Name */}
                <div className="flex flex-col">
                  <h1 className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-[#BA772B] via-[#BA772B] to-[#BA772B] dark:from-yellow-400 dark:via-amber-400 dark:to-orange-400 bg-clip-text text-transparent group-hover:from-amber-600 group-hover:via-orange-500 group-hover:to-yellow-600 dark:group-hover:from-yellow-300 dark:group-hover:via-amber-300 dark:group-hover:to-orange-300 transition-all duration-300 leading-tight">
                    Dev Bhoomi Group
                  </h1>
                  <span className="text-sm sm:text-base lg:text-md font-semibold text-gray-500 dark:text-gray-300 tracking-wider group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors duration-300">
                    COMPANIES
                  </span>
                  <div className="h-0.5 bg-gradient-to-r from-amber-500 to-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left mt-1"></div>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <NavLink key={link.href} href={link.href} label={link.label} />
              ))}

              {/* Dark Mode Button */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="ml-4 p-2 border-2 border-amber-600 dark:border-yellow-500 text-amber-600 dark:text-yellow-500 rounded-full hover:bg-amber-600 dark:hover:bg-yellow-500 hover:text-white dark:hover:text-black transition-all duration-300 group"
                aria-label="Toggle Dark Mode"
              >
                {darkMode ? (
                  <Sun className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                ) : (
                  <Moon className="w-5 h-5 group-hover:-rotate-12 transition-transform" />
                )}
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={toggleSidebar}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 focus:outline-none transition-colors duration-300"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                <Menu className="h-6 w-6" />
              </button>
              {/* Dark Mode Toggle */}
              <div className="flex justify-center sm:justify-end sm:mt-0">
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className="flex items-center space-x-2 px-3 py-2 border-2 border-amber-600 dark:border-yellow-500 text-amber-600 dark:text-yellow-500 rounded-full hover:bg-amber-600 dark:hover:bg-yellow-500 hover:text-white dark:hover:text-black transition-all duration-300 group"
                  aria-label="Toggle Dark Mode"
                >
                  {darkMode ? (
                    <Sun className="w-5 h-5" />
                  ) : (
                    <Moon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white dark:bg-gray-900 shadow-lg dark:shadow-gray-800/30 transform transition-transform duration-300 ease-in-out z-50 ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="p-4 flex flex-col h-full">
          {/* Close button */}
          <div className="flex justify-end">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none transition-colors duration-300"
            >
              <X className="h-6 w-6 text-gray-700 dark:text-gray-300 hover:rotate-90 transition-transform" />
            </button>
          </div>

          {/* Logo in sidebar */}
          <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12">
            <div className="flex sm:items-center justify-evenly my-6">
              {/* Logo Section */}
              <Link to="/" className="flex justify-center sm:justify-start">
                <img
                  src="https://ik.imagekit.io/zfayt6inj/lofgodb.jpg?updatedAt=1748852043440"
                  alt="Dev Bhoomi Group Logo"
                  className="h-12 object-contain cursor-pointer transition-transform duration-300 hover:scale-105"
                />
              </Link>
            </div>
          </div>

          {/* Dark Mode Toggle in Mobile Menu */}
          {/* <div className="flex items-center justify-center mt-4">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="flex items-center space-x-2 px-2 py-2 border-2 border-amber-600 dark:border-yellow-500 text-amber-600 dark:text-yellow-500 rounded-full hover:bg-amber-600 dark:hover:bg-yellow-500 hover:text-white dark:hover:text-black transition-all duration-300 group"
              aria-label="Toggle Dark Mode"
            >
              {darkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
              <span>{darkMode ? "Light" : "Dark"}</span>
            </button>
          </div> */}

          {/* Mobile Navigation Links */}
          <div className="flex flex-col items-start space-y-4 mt-8 px-4">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-left"
              >
                <NavLink href={link.href} label={link.label} mobile={true} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overlay for mobile sidebar */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-70 z-40"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
};

export default Navbar;
